/**
 * SettingsPage - Einstellungen, Onboarding & OAuth-Verbindungen
 *
 * Struktur (von oben nach unten):
 * 1. Fehler-Warnbanner (konditionell - bei 3+ Fehlern pro Plattform)
 * 2. Onboarding-Box (aufklappbar, 8-Punkte-Anleitung)
 * 3. Verbindungsstatus-Block (kompakte Übersicht aller Plattformen)
 * 4. System-Status (API Health)
 * 5. Detaillierte OAuth-Verbindungen (FB, LinkedIn, TikTok)
 * 6. Google My Business
 * 7. Auto-Scheduler
 * 8. 14-Tage-Testmodus
 * 9. Programmierzugänge (API-Schlüssel für Claude/ChatGPT/CLI)
 */

import { useState, useEffect, useCallback, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import VisualDebugPanel from '../components/VisualDebugPanel';
import PrivacyDataSection from '../components/PrivacyDataSection';
import PasswordSection from '../components/PasswordSection';
import ApiKeysSection from '../components/ApiKeysSection';
import {
  Linkedin, RefreshCw, CheckCircle, Loader2, Play, Calendar,
  AlertTriangle, Lightbulb, TrendingUp, Clock, Sparkles,
  Facebook, XCircle, ChevronDown, ChevronUp, Link2, Link2Off,
  Info, Power, List, BookOpen, ArrowRight,
  Globe, Search, Tag, Users, MapPin, MessageSquare, Lightbulb as IdeaIcon,
  Palette, Image, RotateCcw, ArrowUpRight
} from "lucide-react";
import { formatRelativeWithAbsolute } from "../lib/relativeTime";
import { useCurrentPlanId } from "../hooks/useFeatureGate";
import { usePlanSelection, useTrialStatus } from "../hooks/usePlanSelection";
import { getPlanById, pricingPlans, trialConfig } from "../data/pricing";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* ─────────────────────────────────────── helper ─── */
const StatusRow = ({ label, connected }) => (
  <div className="flex items-center justify-between p-3 bg-stone-50 rounded-lg">
    <span className="font-medium">{label}</span>
    <span className={`flex items-center gap-2 text-sm ${connected ? "text-green-600" : "text-red-500"}`}>
      <span className={`w-2 h-2 rounded-full ${connected ? "bg-green-500" : "bg-red-500"}`} />
      {connected ? "Verbunden" : "Nicht konfiguriert"}
    </span>
  </div>
);

/* ──────────────────────── 2a. AKTUELLER PLAN ────── */
//
// Zeigt den aktuell gewählten Plan und den Frontend-Trial-Status.
// Liest aus localStorage via useCurrentPlanId() und useTrialStatus().
// Plan-Infos stammen aus pricing.js — keine Hardcodings.

// selectedPlanId und hasExplicitPlan werden von SettingsPage als Props übergeben,
// damit CurrentPlanSection und PlanComparisonSection dieselbe usePlanSelection-Instanz
// teilen und nach selectPlan() synchron re-rendern.
const CurrentPlanSection = ({ selectedPlanId, hasExplicitPlan }) => {
  const fallbackPlanId = useCurrentPlanId();
  const currentPlanId = selectedPlanId ?? fallbackPlanId;
  const currentPlan = getPlanById(currentPlanId);
  const { isActive, daysRemaining } = useTrialStatus();

  // Serverautoritativer Plan-Status (Quelle der Wahrheit) — der localStorage-Plan ist
  // nur Vorschau. So sieht ein zahlender/abgelaufener Kunde IMMER den echten Zustand.
  const [serverPlan, setServerPlan] = useState(null);
  useEffect(() => {
    let active = true;
    axios.get(`${API}/billing/plan`)
      .then((res) => { if (active) setServerPlan(res.data || null); })
      .catch(() => { /* fail-open: lokale Vorschau bleibt */ });
    return () => { active = false; };
  }, []);

  const purchased = serverPlan?.purchased === true || serverPlan?.owner === true;
  const isOwner = serverPlan?.owner === true;
  const trialActiveServer = serverPlan?.trial_active === true;
  // Server kennt den echten Zustand → er gewinnt; ohne Server-Daten Fallback auf lokale Uhr.
  const trialExpired = serverPlan ? (!purchased && !trialActiveServer) : (hasExplicitPlan && !isActive);
  const serverPlanName = serverPlan?.name;
  const cancelPending = serverPlan?.cancel_at_period_end === true;

  // §312k Kündigungsbutton — direkt in der App, ein Klick + Bestätigung.
  const [canceling, setCanceling] = useState(false);
  const handleCancel = async () => {
    if (!window.confirm("Möchtest du dein Abo wirklich kündigen? Dein Zugang bleibt bis zum Ende der bezahlten Periode aktiv.")) return;
    setCanceling(true);
    try {
      const res = await axios.post(`${API}/billing/cancel`);
      toast.success(res.data?.message || "Kündigung vorgemerkt.");
      setServerPlan((p) => (p ? { ...p, cancel_at_period_end: true } : p));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kündigung konnte nicht verarbeitet werden.");
    } finally {
      setCanceling(false);
    }
  };

  // PAY-03 / DOC-03: Zahlungsdaten ändern, Rechnungen einsehen, kündigen — im
  // Stripe-Kundenportal. Vorher gab es dafür keinen Ort in der App: ein Kunde mit
  // fehlgeschlagener Zahlung kam über „Plan ändern" nur auf /pricing und dort in
  // einen 409 („Dieser Tarif ist bereits aktiv") — eine echte Sackgasse.
  const [openingPortal, setOpeningPortal] = useState(false);
  const handleOpenPortal = async () => {
    setOpeningPortal(true);
    try {
      const res = await axios.post(`${API}/billing/portal`);
      if (res.data?.url) {
        window.location.href = res.data.url;
        return;
      }
      toast.error("Die Zahlungsverwaltung konnte nicht geöffnet werden.");
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail
        : "Die Zahlungsverwaltung konnte nicht geöffnet werden.");
    } finally {
      setOpeningPortal(false);
    }
  };

  return (
    <div className="bg-white rounded-xl border border-stone-200 p-6 space-y-4" data-testid="current-plan-section">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="w-8 h-8 bg-orange-50 rounded-lg flex items-center justify-center flex-shrink-0">
          <Tag size={16} className="text-fox-fire" />
        </div>
        <h2 className="font-heading font-bold text-lg text-stone-800">Dein aktueller Plan</h2>
      </div>

      {/* Plan-Info + Wechsel-Link */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="space-y-0.5">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-stone-800">
              {(purchased && serverPlanName) || currentPlan?.name || "Kein Plan"}
            </span>
            {purchased ? (
              <span className="text-xs px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded-full">
                Aktiv
              </span>
            ) : !hasExplicitPlan && (
              <span className="text-xs px-2 py-0.5 bg-stone-100 text-stone-500 rounded-full">
                Vorschau
              </span>
            )}
          </div>
          <p className="text-sm text-stone-500 leading-snug">
            {purchased
              ? "Dein Paket ist aktiv — alle Kanäle verbindest du über den Verbindungsassistenten."
              : currentPlan?.description ?? "Noch kein Plan ausgewählt."}
          </p>
        </div>
        <Link
          to={`/pricing${currentPlan ? `?highlight=${currentPlan.id}` : ""}`}
          className="flex-shrink-0 flex items-center gap-1.5 text-sm text-fox-fire hover:underline font-medium transition-colors"
        >
          {purchased ? "Plan ändern" : "Plan wählen"}
          <ArrowRight size={14} />
        </Link>
      </div>

      {/* Plan-/Trial-Status — serverautoritativ */}
      <div
        className={`flex items-start gap-3 px-4 py-3 rounded-xl border ${
          purchased
            ? "bg-emerald-50 border-emerald-200"
            : trialActiveServer || isActive
            ? "bg-emerald-50 border-emerald-200"
            : trialExpired
            ? "bg-amber-50 border-amber-200"
            : "bg-stone-50 border-stone-200"
        }`}
      >
        <Clock
          size={15}
          className={`flex-shrink-0 mt-0.5 ${
            purchased || trialActiveServer || isActive ? "text-emerald-600"
            : trialExpired ? "text-amber-600" : "text-stone-400"}`}
        />
        <div className="space-y-0.5">
          {purchased ? (
            <>
              <p className="text-sm font-medium text-emerald-700">Paket aktiv</p>
              <p className="text-xs text-emerald-600">
                Danke für dein Vertrauen — dein Zugang läuft ohne Unterbrechung weiter.
              </p>
            </>
          ) : (trialActiveServer || isActive) ? (
            <>
              <p className="text-sm font-medium text-emerald-700">
                Testphase aktiv{daysRemaining != null ? ` — noch ${daysRemaining} ${daysRemaining === 1 ? "Tag" : "Tage"}` : ""}
              </p>
              <p className="text-xs text-emerald-600">{trialConfig.disclaimer}</p>
            </>
          ) : trialExpired ? (
            <>
              <p className="text-sm font-medium text-amber-700">Deine Testphase ist beendet</p>
              <p className="text-xs text-amber-600">
                Wähle dein Paket, um weiter zu veröffentlichen — deine Inhalte und Verbindungen bleiben erhalten.
              </p>
            </>
          ) : (
            <>
              <p className="text-sm font-medium text-stone-600">{trialConfig.shortLabel}</p>
              <p className="text-xs text-stone-400">
                Wähle einen Plan, um die Testphase zu starten — kein Zahlungsmittel nötig.
              </p>
            </>
          )}
        </div>
      </div>

      {/* Zahlungsdaten & Rechnungen (PAY-03/DOC-03) — nur für zahlende Kunden.
          Bewusst NICHT für den Betreiber: dort gibt es keinen Stripe-Kunden. */}
      {purchased && !isOwner && (
        <div className="pt-2 border-t border-stone-100">
          <button
            type="button"
            onClick={handleOpenPortal}
            disabled={openingPortal}
            data-testid="open-billing-portal"
            className="flex items-center gap-1.5 text-sm text-fox-fire hover:underline font-medium disabled:opacity-50"
          >
            {openingPortal ? "Wird geöffnet…" : "Zahlungsdaten & Rechnungen verwalten"}
            <ArrowRight size={14} />
          </button>
          <p className="text-xs text-stone-400 mt-1 leading-snug">
            Karte wechseln, Rechnungen herunterladen oder kündigen — sicher bei unserem
            Zahlungsdienstleister Stripe.
          </p>
        </div>
      )}

      {/* §312k Kündigungsbutton — nur bei aktivem, gekauftem Abo (nicht Owner/Trial) */}
      {purchased && !isOwner && (
        <div className="pt-2 border-t border-stone-100">
          {cancelPending ? (
            <p className="text-xs text-stone-500">
              Deine Kündigung ist vorgemerkt — dein Zugang bleibt bis zum Ende der bezahlten Periode aktiv.
            </p>
          ) : (
            <button
              type="button"
              onClick={handleCancel}
              disabled={canceling}
              data-testid="cancel-subscription"
              className="text-xs text-stone-500 hover:text-red-600 hover:underline disabled:opacity-50"
            >
              {canceling ? "Wird gekündigt…" : "Abo kündigen"}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

/* ──────────────────────── 2b. PLAN-VERGLEICH ───── */
//
// Kompakte Vergleichssicht aller Pläne + direkter Plan-Wechsel + Planfinder-Einstieg.
// selectedPlanId aus usePlanSelection() ist die reaktive Quelle — aktualisiert sich
// nach selectPlan() sofort ohne Page-Reload.
// useCurrentPlanId() aus useFeatureGate liest nur einmalig beim Mount — hier nicht
// verwendet, da wir nach Plan-Wechsel sofortiges Re-Render brauchen.

// Props kommen von SettingsPage — eine einzige usePlanSelection-Instanz für die
// gesamte Seite, damit CurrentPlanSection und PlanComparisonSection synchron rendern.
const PlanComparisonSection = ({ selectedPlanId, hasExplicitPlan, selectPlan }) => {
  const navigate = useNavigate();

  // Fallback "business" nur für Markierungslogik (Dev-Vorschau-Parität).
  const currentPlanId = selectedPlanId ?? "business";

  // Schlüsseldimensionen für den kompakten Vergleich.
  // Alle Werte aus plan.limits / plan.featureGates — keine Hardcodings.
  const getDimensions = (plan) => [
    {
      label: "Kanäle",
      value:
        plan.limits.channels === null
          ? plan.requestOnly ? "Individuell" : "Alle verfügbaren"
          : `${plan.limits.channels}`,
      boolean: false,
    },
    {
      label: "Beiträge / Tag je Kanal",
      value:
        plan.limits.postsPerDayPerChannel === null
          ? plan.requestOnly ? "Individuell" : "—"
          : `${plan.limits.postsPerDayPerChannel}`,
      boolean: false,
    },
    {
      label: "Marken",
      value:
        plan.limits.brands === null
          ? "Individuell"
          : `${plan.limits.brands} ${plan.limits.brands === 1 ? "Marke" : "Marken"}`,
      boolean: false,
    },
    {
      label: "Team & Clients",
      value: plan.featureGates.team_access,
      boolean: true,
    },
  ];

  return (
    <div
      id="plan-management"
      className="bg-white rounded-xl border border-stone-200 p-6 space-y-5"
      data-testid="plan-comparison-section"
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-orange-50 rounded-lg flex items-center justify-center flex-shrink-0">
            <RotateCcw size={16} className="text-fox-fire" />
          </div>
          <div>
            <h2 className="font-heading font-bold text-lg text-stone-800">
              Plan wechseln
            </h2>
            <p className="text-sm text-stone-500 mt-0.5">
              Direkt wechseln oder den Planfinder erneut starten.
            </p>
          </div>
        </div>
        <button
          onClick={() => navigate("/planfinder?source=settings")}
          className="flex items-center gap-1.5 text-sm text-stone-500 hover:text-fox-fire border border-stone-200 hover:border-stone-300 px-3 py-1.5 rounded-lg transition-all flex-shrink-0"
        >
          <RotateCcw size={12} />
          Plan neu ermitteln
        </button>
      </div>

      {/* Hinweis: noch kein Plan explizit gewählt */}
      {!hasExplicitPlan && (
        <div className="flex items-start gap-2.5 px-3 py-2.5 bg-amber-50 border border-amber-200 rounded-lg">
          <Info size={14} className="text-amber-600 flex-shrink-0 mt-0.5" />
          <p className="text-xs text-amber-700 leading-relaxed">
            Du hast noch keinen Plan aktiv gewählt. Wähle einen Plan unten —
            die {trialConfig.days}-tägige Testphase startet danach automatisch.
          </p>
        </div>
      )}

      {/* Plan-Karten-Grid — 4 sichtbare Stufen (Starter · Wachstum · Dominanz · Enterprise) */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
        {pricingPlans.filter((p) => !p.hidden).map((plan) => {
          const isCurrent = plan.id === currentPlanId;
          const dims = getDimensions(plan);

          return (
            <div
              key={plan.id}
              data-testid={`plan-card-${plan.id}`}
              className={`relative rounded-xl border-2 p-4 transition-all ${
                isCurrent
                  ? "border-fox-fire bg-orange-50/30"
                  : "border-stone-200 bg-white hover:border-stone-300"
              }`}
            >
              {/* Aktuell-Badge */}
              {isCurrent && (
                <span className="absolute top-3 right-3 text-xs px-2 py-0.5 bg-fox-fire text-white rounded-full font-semibold">
                  Aktuell
                </span>
              )}

              {/* Name + Beschreibung */}
              <div className={`mb-2 ${isCurrent ? "pr-16" : ""}`}>
                <p className="font-semibold text-stone-800 text-sm">{plan.name}</p>
                <p className="text-xs text-stone-400 mt-0.5 leading-snug line-clamp-2">
                  {plan.description}
                </p>
              </div>

              {/* Preis — Anfrage-Stufen (Enterprise) zeigen ihr priceLabel statt einer Zahl */}
              <p className="font-heading font-bold text-stone-900 mb-3">
                {plan.priceLabel ? (
                  <>
                    {plan.priceLabel}
                    <span className="text-xs font-normal text-stone-400"> · auf Anfrage</span>
                  </>
                ) : (
                  <>
                    {plan.priceMonthly} €
                    <span className="text-xs font-normal text-stone-400"> /Monat</span>
                  </>
                )}
              </p>

              {/* Schlüsseldimensionen */}
              <div className="space-y-1.5 mb-4">
                {dims.map((dim) => (
                  <div
                    key={dim.label}
                    className="flex items-center justify-between text-xs"
                  >
                    <span className="text-stone-500">{dim.label}</span>
                    {dim.boolean ? (
                      <span
                        className={
                          dim.value
                            ? "text-emerald-600 font-semibold"
                            : "text-stone-300"
                        }
                      >
                        {dim.value ? "✓" : "−"}
                      </span>
                    ) : (
                      <span className="text-stone-700 font-medium">{dim.value}</span>
                    )}
                  </div>
                ))}
              </div>

              {/* CTA */}
              {isCurrent ? (
                <div className="flex items-center gap-1.5 text-xs text-fox-fire font-medium">
                  <CheckCircle size={13} />
                  Aktueller Plan
                </div>
              ) : (
                <button
                  onClick={() => selectPlan(plan.id)}
                  className="w-full py-2 rounded-lg text-xs font-semibold border-2 border-stone-200 text-stone-600 hover:border-fox-fire hover:text-fox-fire hover:bg-orange-50 transition-all"
                >
                  {/* Anfrage-Stufen: selectPlan führt in den Kontakt-Flow statt in den Checkout */}
                  {plan.requestOnly ? plan.cta : `Zu ${plan.name} wechseln`}
                </button>
              )}
            </div>
          );
        })}
      </div>

      {/* Footer: Link zur vollständigen PricingPage */}
      <div className="flex items-center justify-between flex-wrap gap-3 pt-2 border-t border-stone-100">
        <p className="text-xs text-stone-400">
          Vollständige Details, Add-ons und Jahrespreise auf der Preisseite.
        </p>
        <Link
          to={`/pricing?highlight=${currentPlanId}`}
          className="text-xs text-fox-fire hover:underline flex items-center gap-1 font-medium"
        >
          Zur Preisseite
          <ArrowRight size={11} />
        </Link>
      </div>
    </div>
  );
};

/* ─────────────────────── 1. FEHLER-WARNBANNER ──── */
const FailureWarningBanner = ({ failures, onScrollToScheduler }) => {
  const warningPlatforms = Object.entries(failures || {})
    .filter(([, v]) => v.warning)
    .map(([k]) => k);

  if (warningPlatforms.length === 0) return null;

  const labels = {
    facebook: "Facebook",
    instagram: "Instagram",
    linkedin: "LinkedIn",
    tiktok: "TikTok",
    google_business: "Google Business",
  };

  return (
    <div
      className="bg-amber-50 border border-amber-300 rounded-xl p-4 flex items-start gap-3"
      data-testid="failure-warning-banner"
    >
      <AlertTriangle className="text-amber-500 flex-shrink-0 mt-0.5" size={20} />
      <div className="flex-1">
        <p className="font-semibold text-amber-800 text-sm">
          Achtung: Wiederholte Posting-Fehler erkannt
        </p>
        <p className="text-amber-700 text-sm mt-1">
          Beim Posten auf{" "}
          <strong>{warningPlatforms.map(p => labels[p] || p).join(", ")}</strong>{" "}
          sind wiederholt Fehler aufgetreten. Bitte Verbindung prüfen oder neu verbinden.
        </p>
        <button
          onClick={onScrollToScheduler}
          className="mt-2 text-xs text-amber-700 underline hover:text-amber-900 flex items-center gap-1"
        >
          Zu den Logs <ArrowRight size={12} />
        </button>
      </div>
    </div>
  );
};

/* ────────────────────── 2. ONBOARDING-BOX ──────── */
const OnboardingBox = () => {
  const [open, setOpen] = useState(false);

  const steps = [
    {
      num: 1,
      title: "Facebook Page verbinden",
      color: "border-blue-500",
      desc: 'Gehe zu "Kanäle verbinden" (linke Navigation) → "Facebook verbinden" klicken → Seite auswählen → Berechtigungen bestätigen.',
      tip: "Instagram wird automatisch mitverbunden, wenn dein Business-Konto verknüpft ist.",
      tipColor: "bg-blue-50 text-blue-800",
    },
    {
      num: 2,
      title: "Google My Business verbinden",
      color: "border-amber-500",
      desc: 'Unter "Kanäle verbinden" → "Google Business verbinden" → Google-Konto auswählen → Business-Profil bestätigen.',
      tip: null,
    },
    {
      num: 3,
      title: "LinkedIn verbinden",
      color: "border-sky-500",
      desc: 'Unter "Kanäle verbinden" → "LinkedIn verbinden" → Einloggen → Berechtigungen bestätigen.',
      tip: null,
    },
    {
      num: 4,
      title: "TikTok verbinden",
      color: "border-gray-700",
      desc: '"Mit TikTok verbinden" klicken → Einloggen → Berechtigungen (Profil + Videos) bestätigen.',
      tip: "TikTok unterstützt nur Video-Posts. Stelle sicher, dass dein Post ein Video enthält.",
      tipColor: "bg-gray-100 text-gray-700",
    },
    {
      num: 5,
      title: "Auto-Scheduler aktivieren",
      color: "border-orange-500",
      desc: 'Scrolle zu "Auto-Scheduler" unten → Toggle-Schalter aktivieren. Der Scheduler prüft alle 5 Minuten auf fällige Posts.',
      tip: 'Tipp: "Jetzt alle fälligen Posts veröffentlichen" startet einen sofortigen Durchlauf.',
      tipColor: "bg-orange-50 text-orange-800",
    },
    {
      num: 6,
      title: "Ersten Post einplanen",
      color: "border-green-500",
      desc: 'Gehe zum Monatsplan → Post erstellen → Datum & Uhrzeit setzen → Status auf "geplant" setzen.',
      tip: null,
    },
    {
      num: 7,
      title: "Fehler prüfen",
      color: "border-red-400",
      desc: 'Fehlgeschlagene Posts zeigen ein rotes ❌ Badge im Monatsplan. Die letzten Ausführungen des Schedulers findest du unter "Letzte Ausführungen".',
      tip: "Häufige Fehler: Token abgelaufen → neu verbinden | TikTok ohne Video | Rate Limit erreicht.",
      tipColor: "bg-red-50 text-red-800",
    },
    {
      num: 8,
      title: "14-Tage-Testmodus nutzen",
      color: "border-purple-500",
      desc: 'Aktiviere den Testmodus unten um detaillierte Logs & KI-Analysen über deine App-Nutzung zu erhalten. Ändert nichts am Live-Betrieb.',
      tip: null,
    },
  ];

  return (
    <div className="bg-white rounded-xl border border-stone-200 overflow-hidden" data-testid="onboarding-box">
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between p-5 hover:bg-stone-50 transition-colors"
        data-testid="onboarding-toggle-btn"
      >
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 bg-orange-100 rounded-lg flex items-center justify-center">
            <BookOpen className="text-orange-500" size={18} />
          </div>
          <div className="text-left">
            <p className="font-semibold text-stone-800">Schnell-Anleitung: So startest du</p>
            <p className="text-xs text-stone-500">8 Schritte vom ersten Connect bis zum automatischen Posting</p>
          </div>
        </div>
        {open ? (
          <ChevronUp className="text-stone-400 flex-shrink-0" size={20} />
        ) : (
          <ChevronDown className="text-stone-400 flex-shrink-0" size={20} />
        )}
      </button>

      {open && (
        <div className="px-5 pb-5 border-t border-stone-100">
          <div className="mt-4 space-y-3">
            {steps.map(step => (
              <div key={step.num} className={`border-l-4 ${step.color} pl-4 py-1`}>
                <p className="font-semibold text-sm text-stone-800">
                  Schritt {step.num}: {step.title}
                </p>
                <p className="text-sm text-stone-600 mt-0.5">{step.desc}</p>
                {step.tip && (
                  <p className={`text-xs mt-1.5 rounded px-2 py-1 inline-block ${step.tipColor}`}>
                    {step.tip}
                  </p>
                )}
              </div>
            ))}
          </div>
          <p className="text-xs text-stone-400 mt-4 text-center">
            Weitere Details unter{" "}
            <a href="/hilfe" className="text-orange-500 underline">
              Hilfe & Anleitung
            </a>
          </p>
        </div>
      )}
    </div>
  );
};

/* ───────────────── 3. VERBINDUNGSSTATUS-BLOCK ───── */
const ConnectionStatusBlock = ({ oauthStatus, gmbStatus }) => {
  const platforms = [
    {
      id: "facebook",
      label: "Facebook",
      icon: <Facebook size={16} />,
      iconBg: "bg-blue-600",
      status: oauthStatus?.facebook?.needs_refresh
        ? "reconnect"
        : oauthStatus?.facebook?.connected
        ? "connected"
        : "disconnected",
      detail: oauthStatus?.facebook?.page_name || null,
    },
    {
      id: "instagram",
      label: "Instagram",
      icon: <span className="text-xs font-bold">IG</span>,
      iconBg: "bg-gradient-to-br from-purple-500 to-pink-500",
      status: oauthStatus?.facebook?.connected && !oauthStatus?.facebook?.needs_refresh
        ? "connected"
        : oauthStatus?.facebook?.needs_refresh
        ? "reconnect"
        : "disconnected",
      detail: oauthStatus?.facebook?.connected ? "Via Facebook" : null,
      noDirectConnect: true, // verbindet via Facebook
    },
    {
      id: "linkedin",
      label: "LinkedIn",
      icon: <Linkedin size={16} />,
      iconBg: "bg-sky-700",
      status: oauthStatus?.linkedin?.status === "reconnect_needed"
        ? "reconnect"
        : oauthStatus?.linkedin?.connected
        ? "connected"
        : "disconnected",
      detail: oauthStatus?.linkedin?.profile_name || null,
    },
    {
      id: "tiktok",
      label: "TikTok",
      icon: <span className="text-xs font-bold">TT</span>,
      iconBg: "bg-gray-900",
      status: oauthStatus?.tiktok?.status === "reconnect_needed" || oauthStatus?.tiktok?.status === "token_expired"
        ? "reconnect"
        : oauthStatus?.tiktok?.connected
        ? "connected"
        : oauthStatus?.tiktok?.not_configured
        ? "pending_review"
        : "disconnected",
      detail: oauthStatus?.tiktok?.profile_name || null,
    },
    {
      id: "gmb",
      label: "Google Business",
      icon: <span className="text-sm font-bold">G</span>,
      iconBg: "bg-amber-500",
      status: gmbStatus ? "connected" : "disconnected",
      detail: null,
    },
  ];

  const statusConfig = {
    connected: { dot: "bg-green-500", text: "text-green-600", label: "Verbunden" },
    disconnected: { dot: "bg-red-400", text: "text-red-500", label: "Nicht verbunden" },
    reconnect: { dot: "bg-amber-500 animate-pulse", text: "text-amber-600", label: "Neu verbinden" },
    pending_review: { dot: "bg-yellow-400", text: "text-yellow-600", label: "Wartet auf Genehmigung" },
  };

  return (
    <div className="bg-white rounded-xl border border-stone-200 p-5" data-testid="connection-status-block">
      <h2 className="font-semibold text-stone-800 mb-3 flex items-center gap-2">
        <Link2 size={16} className="text-stone-500" />
        Verbindungsstatus – alle Plattformen
      </h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
        {platforms.map(p => {
          const cfg = statusConfig[p.status];
          return (
            <div
              key={p.id}
              className="flex items-center gap-3 p-3 bg-stone-50 rounded-lg"
              data-testid={`platform-status-${p.id}`}
            >
              <div className={`w-8 h-8 ${p.iconBg} rounded-md flex items-center justify-center text-white flex-shrink-0`}>
                {p.icon}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-stone-800">{p.label}</p>
                <div className="flex items-center gap-1.5 mt-0.5">
                  <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} />
                  <span className={`text-xs ${cfg.text}`}>{cfg.label}</span>
                  {p.detail && (
                    <span className="text-xs text-stone-400 truncate">· {p.detail}</span>
                  )}
                </div>
              </div>
              {/* CTA — leitet zu /kanale weiter (zentrale Verbindungsseite) */}
              {(p.status === "reconnect" || p.status === "disconnected") && !p.noDirectConnect && p.id !== "instagram" && (
                <a
                  href="/kanale"
                  className={`text-xs font-medium flex-shrink-0 ${
                    p.status === "reconnect"
                      ? "text-amber-600 hover:text-amber-700"
                      : "text-blue-600 hover:text-blue-700"
                  }`}
                  data-testid={`status-block-connect-${p.id}`}
                >
                  {p.status === "reconnect" ? <RefreshCw size={12} /> : <ArrowRight size={12} />}
                </a>
              )}
              {p.status === "connected" && !p.noDirectConnect && (
                <CheckCircle size={14} className="text-green-500 flex-shrink-0" />
              )}
            </div>
          );
        })}
      </div>
      <p className="text-xs text-stone-400 mt-3">
        Instagram wird automatisch via Facebook verbunden. TikTok wartet auf Developer-App-Genehmigung.
      </p>
    </div>
  );
};

/* ─────────────────── TEST MODE SECTION ─────────── */
const TestModeSection = () => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activating, setActivating] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split("T")[0]);
  const [summary, setSummary] = useState(null);
  const [insights, setInsights] = useState(null);
  const [loadingData, setLoadingData] = useState(false);

  const loadStatus = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/test-mode/status`);
      setStatus(res.data);
    } catch {
      /* ignore */
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  useEffect(() => {
    if (status?.active && selectedDate) {
      (async () => {
        setLoadingData(true);
        try {
          const [sRes, iRes] = await Promise.all([
            axios.get(`${API}/test-mode/daily-summary?date=${selectedDate}`),
            axios.get(`${API}/test-mode/daily-insights?date=${selectedDate}`),
          ]);
          setSummary(sRes.data);
          setInsights(iRes.data);
        } catch { /* ignore */ } finally {
          setLoadingData(false);
        }
      })();
    }
  }, [status?.active, selectedDate]);

  const handleActivate = async () => {
    setActivating(true);
    try {
      await axios.post(`${API}/test-mode/activate`);
      toast.success("14-Tage-Testmodus aktiviert!");
      loadStatus();
    } catch { toast.error("Fehler beim Aktivieren"); }
    finally { setActivating(false); }
  };

  const handleDeactivate = async () => {
    try {
      await axios.post(`${API}/test-mode/deactivate`);
      toast.success("Testmodus deaktiviert");
      loadStatus();
    } catch { toast.error("Fehler beim Deaktivieren"); }
  };

  if (loading) return (
    <div className="bg-white rounded-xl p-6 border border-stone-200 flex items-center justify-center h-32">
      <Loader2 className="animate-spin text-purple-500" />
    </div>
  );

  return (
    <div className="bg-white rounded-xl p-6 border border-stone-200 space-y-6" data-testid="test-mode-section">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <Clock className="text-purple-500" />
            14-Tage-Testmodus
          </h3>
          <p className="text-sm text-stone-500 mt-1">
            Beobachte wie gut dein Assistent arbeitet – ohne etwas am Live-Betrieb zu ändern
          </p>
        </div>
        {status?.active && (
          <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-medium flex items-center gap-1">
            <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
            Aktiv
          </span>
        )}
      </div>

      {status?.active ? (
        <div className="bg-purple-50 rounded-lg p-4 border border-purple-200">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
            <div><p className="text-2xl font-bold text-purple-600">{status.days_left}</p><p className="text-xs text-purple-500">Tage verbleibend</p></div>
            <div><p className="text-2xl font-bold text-purple-600">{status.days_elapsed}</p><p className="text-xs text-purple-500">Tage aktiv</p></div>
            <div><p className="text-2xl font-bold text-purple-600">{status.total_events}</p><p className="text-xs text-purple-500">Events geloggt</p></div>
            <div><p className="text-sm text-purple-600">{status.end_date?.split("T")[0]}</p><p className="text-xs text-purple-500">Endet am</p></div>
          </div>
          <p className="text-xs text-purple-600 mt-3 text-center">Alle Posts werden weiterhin live veröffentlicht. Nur Statistiken werden erfasst.</p>
        </div>
      ) : (
        <div className="bg-stone-50 rounded-lg p-6 text-center">
          <Sparkles className="mx-auto text-stone-300 mb-3" size={40} />
          <p className="text-stone-600 mb-4">Starte den Testmodus um zu sehen, wie gut dein KI Social Media Manager für dich arbeitet.</p>
          <button
            onClick={handleActivate}
            disabled={activating}
            className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 flex items-center gap-2 mx-auto"
            data-testid="activate-test-mode-btn"
          >
            {activating ? <Loader2 size={16} className="animate-spin" /> : <Play size={16} />}
            14-Tage-Testmodus starten
          </button>
        </div>
      )}

      {status?.active && (
        <>
          <div className="border-t border-stone-200 pt-4">
            <div className="flex items-center justify-between mb-4">
              <h4 className="font-medium text-stone-700">Tagesreport</h4>
              <div className="flex items-center gap-2">
                <button onClick={() => { const y = new Date(); y.setDate(y.getDate()-1); setSelectedDate(y.toISOString().split("T")[0]); }} className="px-3 py-1 text-sm bg-stone-100 rounded hover:bg-stone-200">Gestern</button>
                <button onClick={() => setSelectedDate(new Date().toISOString().split("T")[0])} className="px-3 py-1 text-sm bg-stone-100 rounded hover:bg-stone-200">Heute</button>
                <input type="date" value={selectedDate} onChange={e => setSelectedDate(e.target.value)} className="text-sm border border-stone-200 rounded px-2 py-1" />
              </div>
            </div>
            {loadingData ? (
              <div className="flex justify-center py-8"><Loader2 className="animate-spin text-purple-500" /></div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {summary && (
                  <div className="bg-stone-50 rounded-lg p-4">
                    <h5 className="font-medium text-stone-700 mb-3 flex items-center gap-2"><TrendingUp size={16} className="text-blue-500" />Zahlen für {selectedDate}</h5>
                    <div className="space-y-2 text-sm">
                      {[["Posts veröffentlicht", summary.posts_published?.total || 0, "text-green-600"],
                        ["Posts fehlgeschlagen", summary.posts_failed?.total || 0, summary.posts_failed?.total > 0 ? "text-red-600" : "text-stone-400"],
                        ["Assistent genutzt", summary.simple_assistant_runs || 0, ""],
                        ["Strategien erstellt", summary.strategies_generated || 0, ""],
                        ["Evergreen-Reposts", summary.evergreen_reposts || 0, ""],
                        ["Inbox-Antworten", summary.inbox_replies || 0, ""],
                      ].map(([label, val, cls]) => (
                        <div key={label} className="flex justify-between">
                          <span className="text-stone-500">{label}</span>
                          <span className={`font-medium ${cls}`}>{val}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {insights?.insights && (
                  <div className="bg-purple-50 rounded-lg p-4">
                    <h5 className="font-medium text-purple-700 mb-3 flex items-center gap-2"><Sparkles size={16} />KI-Analyse</h5>
                    <div className="space-y-2">
                      {insights.insights.map((ins, idx) => (
                        <div key={idx} className="flex items-start gap-2 text-sm">
                          {ins.type === "success" && <CheckCircle size={16} className="text-green-500 flex-shrink-0 mt-0.5" />}
                          {ins.type === "warning" && <AlertTriangle size={16} className="text-amber-500 flex-shrink-0 mt-0.5" />}
                          {ins.type === "tip" && <Lightbulb size={16} className="text-blue-500 flex-shrink-0 mt-0.5" />}
                          <span className="text-purple-800">{ins.text}</span>
                        </div>
                      ))}
                    </div>
                    {insights.fallback && <p className="text-xs text-purple-500 mt-2">Basis-Analyse (KI nicht verfügbar)</p>}
                  </div>
                )}
              </div>
            )}
          </div>
          <div className="border-t border-stone-200 pt-4 flex justify-end">
            <button onClick={handleDeactivate} className="px-4 py-2 text-sm text-stone-500 hover:text-red-600 hover:bg-red-50 rounded">
              Testmodus beenden
            </button>
          </div>
        </>
      )}
    </div>
  );
};

/* ───────────────── AUTO-SCHEDULER SECTION ───────── */
const AutoSchedulerSection = ({ schedulerRef }) => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [toggling, setToggling] = useState(false);
  const [history, setHistory] = useState([]);

  const loadStatus = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/auto-scheduler/status`);
      setStatus(res.data);
      setHistory(res.data.recent_runs || []);
    } catch {
      setStatus({ enabled: false, interval_minutes: 5 });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  const toggleScheduler = async () => {
    setToggling(true);
    try {
      const newState = !status?.enabled;
      await axios.post(`${API}/auto-scheduler/toggle`, { enabled: newState });
      toast.success(newState ? "Auto-Scheduler aktiviert!" : "Auto-Scheduler deaktiviert");
      loadStatus();
    } catch { toast.error("Fehler beim Umschalten des Schedulers"); }
    finally { setToggling(false); }
  };

  const runNow = async () => {
    try {
      toast.info("Starte manuellen Durchlauf...");
      const res = await axios.post(`${API}/auto-scheduler/run-now`);
      toast.success(`${res.data.published_count || 0} Posts veröffentlicht, ${res.data.failed_count || 0} fehlgeschlagen`);
      loadStatus();
    } catch { toast.error("Fehler beim manuellen Durchlauf"); }
  };

  if (loading) return (
    <div className="bg-white rounded-xl border border-stone-200 p-6 flex items-center justify-center py-8">
      <Loader2 className="animate-spin text-orange-500" size={24} />
    </div>
  );

  return (
    <div ref={schedulerRef} className="bg-gradient-to-br from-orange-50 to-amber-50 rounded-xl border border-orange-200 p-6" data-testid="auto-scheduler-section">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="font-heading text-lg font-bold text-orange-900 flex items-center gap-2">
            <Clock size={20} />
            Auto-Scheduler
          </h2>
          <p className="text-sm text-orange-700 mt-1">Geplante Posts werden automatisch zur eingestellten Zeit veröffentlicht.</p>
        </div>
        <button
          onClick={toggleScheduler}
          disabled={toggling}
          className={`relative w-14 h-7 rounded-full transition-colors ${status?.enabled ? "bg-green-500" : "bg-gray-300"}`}
          data-testid="auto-scheduler-toggle"
          aria-label="Auto-Scheduler umschalten"
        >
          <span className={`absolute top-1 w-5 h-5 bg-white rounded-full shadow transition-transform ${status?.enabled ? "left-8" : "left-1"}`} />
        </button>
      </div>

      <div className="bg-white rounded-lg p-4 mb-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`w-3 h-3 rounded-full ${status?.enabled ? "bg-green-500 animate-pulse" : "bg-gray-300"}`} />
            <span className="font-medium">{status?.enabled ? "Aktiv" : "Deaktiviert"}</span>
          </div>
          <span className="text-sm text-stone-500">Intervall: alle {status?.interval_minutes || 5} Minuten</span>
        </div>
        {status?.enabled && status?.next_run && (
          <p className="text-xs text-stone-500 mt-2">Nächster Durchlauf: {new Date(status.next_run).toLocaleString("de-DE")}</p>
        )}
      </div>

      <button
        onClick={runNow}
        className="w-full py-2 bg-fox-fire text-white rounded-lg hover:bg-fox-fire-dark flex items-center justify-center gap-2 text-sm font-medium mb-4"
        data-testid="run-scheduler-now"
      >
        <Play size={16} />
        Jetzt alle fälligen Posts veröffentlichen
      </button>

      {history.length > 0 && (
        <div className="bg-white rounded-lg p-4" data-testid="scheduler-history">
          <h3 className="font-medium text-stone-700 mb-3 flex items-center gap-2">
            <List size={16} />
            Letzte Ausführungen
          </h3>
          <div className="space-y-2 max-h-48 overflow-y-auto">
            {history.slice(0, 10).map((run, idx) => (
              <div key={idx} className="flex items-center justify-between text-sm p-2 bg-stone-50 rounded">
                <span className="text-stone-600">{new Date(run.timestamp).toLocaleString("de-DE", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}</span>
                <div className="flex items-center gap-3">
                  <span className="text-green-600 flex items-center gap-1"><CheckCircle size={12} /> {run.published || 0}</span>
                  {run.failed > 0 && <span className="text-red-600 flex items-center gap-1"><XCircle size={12} /> {run.failed}</span>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="mt-4 p-3 bg-white/60 rounded-lg text-xs text-stone-600">
        <strong>Hinweis:</strong> Der Scheduler prüft alle 5 Minuten ob Posts für die aktuelle Zeit geplant sind.
        Posts mit <code>status: scheduled</code> und <code>scheduled_at</code> ≤ jetzt werden automatisch veröffentlicht.
      </div>
    </div>
  );
};

/* ─────────────────────── MAIN PAGE ─────────────── */
// ─── Website Insights Panel ───────────────────────────────────────────────────
//
// Onboarding-Feature: Nutzer gibt Website-URL an → System analysiert öffentliche
// Inhalte → zeigt BrandSnapshot + Content-Themen mit Post-Ideen.

// Hinweis: Die frühere lokale Helferfunktion _formatDate ("DD.MM.YYYY, HH:MM Uhr")
// ist nach ../lib/relativeTime umgezogen (formatAbsoluteTime) und wird hier über
// formatRelativeWithAbsolute genutzt — gleiche Zeitangaben in der ganzen App.

// ── Logo Preview mit onError-Fallback ────────────────────────────────────────
const LogoPreview = ({ url }) => {
  const [hasError, setHasError] = useState(false);
  return (
    <div className="w-12 h-12 rounded-lg border border-stone-200 bg-stone-50
                    flex items-center justify-center overflow-hidden flex-shrink-0">
      {hasError ? (
        <Image size={18} className="text-stone-300" />
      ) : (
        <img
          src={url}
          alt="Logo"
          className="w-full h-full object-contain p-1"
          onError={() => setHasError(true)}
        />
      )}
    </div>
  );
};

// ── Visuelles Markenprofil — Farbkacheln + Logo-Preview ──────────────────────
const VisualBrandSection = ({ visual }) => {
  if (!visual) return null;

  const colorItems = [
    { label: "Primärfarbe",    color: visual.primary_color },
    { label: "Sekundärfarbe",  color: visual.secondary_color },
    { label: "Hintergrund",    color: visual.background_color },
  ].filter(item => item.color);

  const hasColors = colorItems.length > 0;
  const hasLogo   = !!visual.logo_url;

  if (!hasColors && !hasLogo) return null;

  return (
    <div>
      <p className="text-xs font-semibold text-stone-500 uppercase tracking-wide mb-3
                    flex items-center gap-1.5">
        <Palette size={12} className="text-stone-400" />
        Visuelles Markenprofil
      </p>

      <div className="rounded-xl border border-stone-200 bg-white p-4 space-y-4">

        {/* Farbkacheln */}
        {hasColors && (
          <div>
            <p className="text-xs font-medium text-stone-500 mb-2">Erkannte Farben</p>
            <div className="flex flex-wrap gap-4">
              {colorItems.map(({ label, color }) => (
                <div key={label} className="flex items-center gap-2">
                  <div
                    className="w-8 h-8 rounded-lg border border-stone-200 shadow-sm flex-shrink-0"
                    style={{ backgroundColor: color }}
                    title={color}
                  />
                  <div>
                    <p className="text-xs font-medium text-stone-600 leading-tight">{label}</p>
                    <p className="text-xs text-stone-400 font-mono leading-tight">{color}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Logo Preview */}
        {hasLogo && (
          <div>
            <p className="text-xs font-medium text-stone-500 mb-2">Logo / Icon</p>
            <div className="flex items-center gap-3">
              <LogoPreview url={visual.logo_url} />
              <p className="text-xs text-stone-400 break-all leading-snug"
                 style={{ maxWidth: 240 }}>
                {visual.logo_url}
              </p>
            </div>
          </div>
        )}

        {/* Stilnotiz */}
        {visual.notes && (
          <p className="text-xs text-stone-400 italic">{visual.notes}</p>
        )}
      </div>
    </div>
  );
};

// ── Wiederverwendbare Profil-Darstellung (BrandSnapshot + ContentThemes) ──────
const BrandProfileCard = ({ snap, themes, label, metaLine, visual }) => (
  <div className="space-y-5">
    {/* Brand Snapshot */}
    <div className="rounded-xl border border-orange-200 bg-orange-50 p-4">
      <div className="flex items-start justify-between gap-2 mb-3">
        <p className="text-xs font-semibold text-orange-500 uppercase tracking-wide">
          {label || "Aktuelles Markenprofil"}
        </p>
        {metaLine && (
          <p className="text-xs text-stone-400 text-right leading-tight">{metaLine}</p>
        )}
      </div>

      <h3 className="font-bold text-stone-800 text-lg leading-tight">
        {snap.brand_name}
      </h3>
      {snap.tagline && (
        <p className="text-sm text-stone-500 italic mt-0.5">„{snap.tagline}"</p>
      )}

      <div className="mt-3 space-y-2">
        <div className="flex items-start gap-2">
          <MessageSquare size={13} className="text-orange-400 mt-0.5 flex-shrink-0" />
          <div>
            <span className="text-xs font-medium text-stone-500">Kommunikationsstil</span>
            <p className="text-sm text-stone-700">{snap.tone_of_voice}</p>
          </div>
        </div>
        <div className="flex items-start gap-2">
          <Users size={13} className="text-orange-400 mt-0.5 flex-shrink-0" />
          <div>
            <span className="text-xs font-medium text-stone-500">Zielgruppe</span>
            <p className="text-sm text-stone-700">{snap.target_audience}</p>
          </div>
        </div>
        {snap.location_or_region && (
          <div className="flex items-start gap-2">
            <MapPin size={13} className="text-orange-400 mt-0.5 flex-shrink-0" />
            <div>
              <span className="text-xs font-medium text-stone-500">Region</span>
              <p className="text-sm text-stone-700">{snap.location_or_region}</p>
            </div>
          </div>
        )}
        {snap.core_offers?.length > 0 && (
          <div className="flex items-start gap-2">
            <Tag size={13} className="text-orange-400 mt-0.5 flex-shrink-0" />
            <div>
              <span className="text-xs font-medium text-stone-500">Kernangebote</span>
              <div className="flex flex-wrap gap-1.5 mt-1">
                {snap.core_offers.map((offer, i) => (
                  <span
                    key={i}
                    className="px-2 py-0.5 bg-white border border-orange-200
                               rounded-full text-xs text-orange-700 font-medium"
                  >
                    {offer}
                  </span>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>

    {/* Content-Themen */}
    {themes?.themes?.length > 0 && (
      <div>
        <p className="text-xs font-semibold text-stone-500 uppercase tracking-wide mb-3">
          Vorgeschlagene Content-Themen
        </p>
        <div className="space-y-3">
          {themes.themes.map((theme, idx) => (
            <div key={idx} className="rounded-xl border border-stone-200 bg-white p-4">
              <div className="flex items-center gap-2 mb-1">
                <IdeaIcon size={14} className="text-amber-500 flex-shrink-0" />
                <p className="font-semibold text-stone-800 text-sm">{theme.name}</p>
              </div>
              <p className="text-xs text-stone-500 mb-2 leading-snug">{theme.description}</p>
              <ul className="space-y-1">
                {theme.example_post_ideas.map((idea, j) => (
                  <li key={j} className="text-xs text-stone-600 flex items-start gap-1.5">
                    <span className="text-fox-fire font-bold mt-0.5">→</span>
                    {idea}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    )}

    {/* Visuelles Markenprofil — nur wenn Daten vorhanden */}
    {visual && <VisualBrandSection visual={visual} />}

    {/* Hinweis: Grenzen der Analyse */}
    <div className="flex items-start gap-2 p-3 bg-stone-50 border border-stone-200 rounded-lg">
      <Info size={13} className="text-stone-400 mt-0.5 flex-shrink-0" />
      <p className="text-xs text-stone-500 leading-snug">
        {visual?.primary_color || visual?.logo_url ? (
          <>
            <strong className="text-stone-600">Hinweis:</strong> Farben und Logo wurden
            automatisch aus CSS/HTML der Website abgeleitet. Für exakte Markenfarben
            empfehlen wir eine manuelle Prüfung im Post-Editor.
          </>
        ) : (
          <>
            <strong className="text-stone-600">Hinweis:</strong> Das Markenprofil basiert
            auf den Texten deiner Website. Markenfarben und Logo konnten nicht automatisch
            erkannt werden (z.&nbsp;B. bei CSS-Framework-lastigen Seiten) — du kannst sie
            im Post-Editor manuell einstellen.
          </>
        )}
      </p>
    </div>
  </div>
);

const WebsiteInsightsPanel = () => {
  const [url, setUrl]               = useState("");
  const [analyzing, setAnalyzing]   = useState(false);
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [storedProfile, setStoredProfile]   = useState(null);  // LatestInsightResponse
  const [freshResult, setFreshResult]       = useState(null);  // WebsiteAnalysisResponse nach Analyse
  const [error, setError]           = useState(null);

  // Beim Laden: gespeichertes Markenprofil abrufen
  useEffect(() => {
    const fetchLatest = async () => {
      try {
        const res = await axios.get(`${API}/website-insights/latest`);
        if (res.data?.has_profile) {
          setStoredProfile(res.data);
        }
      } catch {
        // Kein gespeichertes Profil vorhanden — kein Fehler anzeigen
      } finally {
        setLoadingProfile(false);
      }
    };
    fetchLatest();
  }, []);

  const handleAnalyze = async () => {
    const trimmed = url.trim();
    if (!trimmed) {
      toast.error("Bitte gib eine Website-URL ein.");
      return;
    }
    setAnalyzing(true);
    setFreshResult(null);
    setError(null);
    try {
      const res = await axios.post(`${API}/website-insights/analyze`, { url: trimmed });
      const data = res.data;
      if (data.analysis_status === "ok") {
        setFreshResult(data);
        // Gespeichertes Profil im Hintergrund aktualisieren
        try {
          const latestRes = await axios.get(`${API}/website-insights/latest`);
          if (latestRes.data?.has_profile) setStoredProfile(latestRes.data);
        } catch { /* nicht kritisch */ }
      } else {
        setError(
          data.message ||
          "Analyse fehlgeschlagen. Bitte überprüfe die URL und versuche es erneut."
        );
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (err.response?.status === 429) {
        setError(detail || "Bitte warte kurz, bevor du dieselbe URL erneut analysierst.");
      } else if (err.response?.status === 422) {
        setError(detail || "Keine gültige URL. Bitte prüfe deine Eingabe.");
      } else {
        setError(
          "Wir konnten deine Website nicht zuverlässig lesen. " +
          "Bitte überprüfe die URL oder probiere es später erneut."
        );
      }
    } finally {
      setAnalyzing(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") handleAnalyze();
  };

  // Anzuzeigendes Profil: frisches Ergebnis hat Vorrang vor gespeichertem
  const displaySnap   = freshResult?.brand_snapshot     ?? storedProfile?.brand_snapshot;
  const displayThemes = freshResult?.content_themes     ?? storedProfile?.content_themes;
  const displayVisual = freshResult?.visual_brand_profile ?? storedProfile?.visual_brand_profile;
  const isStoredView  = !freshResult && !!storedProfile?.has_profile;

  return (
    <div className="bg-white rounded-xl border border-stone-200 overflow-hidden">
      {/* Header */}
      <div className="p-5 border-b border-stone-100">
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 bg-orange-100 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
            <Globe size={18} className="text-orange-500" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-stone-800">Website analysieren</p>
            <p className="text-xs text-stone-500">
              Gib deine Website-URL ein — der KI Social Media Manager liest
              deine öffentlichen Inhalte und erstellt ein Markenprofil.
            </p>
          </div>
          <Link
            to="/website-optimierung"
            className="flex-shrink-0 inline-flex items-center gap-1 text-xs font-medium text-fox-fire hover:text-orange-700 transition-colors mt-0.5"
          >
            Optimierung starten
            <ArrowUpRight size={12} />
          </Link>
        </div>
      </div>

      {/* Eingabe + Aktionen */}
      <div className="p-5 space-y-4">
        <div className="flex gap-2">
          <input
            type="url"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={storedProfile?.raw_url || "https://deine-website.de"}
            disabled={analyzing}
            className="flex-1 px-3 py-2 rounded-lg border border-stone-200 text-sm
                       focus:outline-none focus:ring-2 focus:ring-orange-300
                       disabled:opacity-50 disabled:cursor-not-allowed"
          />
          <button
            onClick={handleAnalyze}
            disabled={analyzing || !url.trim()}
            className="flex items-center gap-2 px-4 py-2 bg-fox-fire text-white
                       rounded-lg text-sm font-medium hover:bg-orange-600
                       transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {analyzing ? (
              <Loader2 size={15} className="animate-spin" />
            ) : (
              <Search size={15} />
            )}
            {analyzing ? "Analysiere…" : "Analysieren"}
          </button>
        </div>

        {/* Hinweis: nur öffentliche Inhalte */}
        <p className="text-xs text-stone-400 flex items-start gap-1.5">
          <Info size={11} className="mt-0.5 flex-shrink-0" />
          Es werden ausschließlich öffentlich sichtbare Inhalte der angegebenen
          Website analysiert.
        </p>

        {/* Fehler */}
        {error && (
          <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-100 rounded-lg">
            <AlertTriangle size={14} className="text-red-400 mt-0.5 flex-shrink-0" />
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        {/* Profil laden… */}
        {loadingProfile && !displaySnap && (
          <div className="flex items-center gap-2 py-4 text-stone-400">
            <Loader2 size={15} className="animate-spin" />
            <span className="text-sm">Lade gespeichertes Profil…</span>
          </div>
        )}

        {/* Profil anzeigen */}
        {displaySnap && (
          <BrandProfileCard
            snap={displaySnap}
            themes={displayThemes}
            visual={displayVisual}
            label={isStoredView ? "Aktuelles Markenprofil" : "So versteht der KI Social Media Manager deine Marke"}
            metaLine={
              // Relativ + absolut: „vor 12 Tagen" allein ist mehrdeutig, ein reines
              // Datum allein beantwortet „ist das noch aktuell?" nicht.
              // analyzed_at ist neu — ältere Profile kennen nur updated_at.
              isStoredView
                ? (() => {
                    const t = formatRelativeWithAbsolute(
                      storedProfile?.analyzed_at || storedProfile?.updated_at,
                    );
                    return t ? `Zuletzt analysiert: ${t}` : null;
                  })()
                : null
            }
          />
        )}

        {/* Kein Profil, kein Laden — Aufforderung */}
        {!loadingProfile && !displaySnap && !analyzing && (
          <p className="text-sm text-stone-400 text-center py-4">
            Noch kein Markenprofil gespeichert. Gib oben deine Website-URL ein.
          </p>
        )}
      </div>
    </div>
  );
};

// ─── Markenprofil bearbeiten ──────────────────────────────────────────────────
// Erfasst im Onboarding, hier nachträglich editierbar. Füttert den 1-Klick-Monatsplan
// (autopilot/one-click) über brand_profile + plan_defaults am app_account.

const BP_TONES = [
  { id: "warm", label: "Warm & nahbar" },
  { id: "professionell", label: "Professionell" },
  { id: "locker", label: "Locker & frech" },
  { id: "inspirierend", label: "Inspirierend" },
  { id: "sachlich", label: "Sachlich" },
];
const BP_GOALS = [
  { id: "Sichtbarkeit", label: "Sichtbarkeit" },
  { id: "Vertrauen", label: "Vertrauen aufbauen" },
  { id: "Buchungen", label: "Mehr Buchungen" },
  { id: "Community", label: "Community stärken" },
  { id: "Events", label: "Events & Aktionen" },
];
const BP_PLATFORMS = [
  { id: "instagram", label: "Instagram" },
  { id: "facebook", label: "Facebook" },
  { id: "linkedin", label: "LinkedIn" },
  { id: "tiktok", label: "TikTok" },
  { id: "google_business", label: "Google Business" },
];
const BP_INTENSITIES = [
  { id: "leicht", label: "Entspannt · ~2/Woche" },
  { id: "normal", label: "Regelmäßig · ~3/Woche" },
  { id: "intensiv", label: "Aktiv · ~5/Woche" },
];
const BP_TOPIC_SUGGESTIONS = [
  "Angebote", "Tipps & Wissen", "Hinter den Kulissen",
  "Kundenstimmen", "Erfolgsgeschichten", "Team", "Aktionen", "FAQ",
];
const _BP_DEFAULT = { company_name: "", offer: "", audience: "", topics: [], tone: "", website: "" };
const _PD_DEFAULT = { goals: ["Sichtbarkeit"], intensity: "normal", platforms: ["instagram", "facebook"] };

const Chip = ({ active, onClick, children }) => (
  <button
    type="button"
    onClick={onClick}
    className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
      active
        ? "bg-fox-fire text-white border-fox-fire"
        : "bg-white text-stone-600 border-stone-200 hover:border-orange-300"
    }`}
  >
    {children}
  </button>
);

const BrandProfileEditor = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [open, setOpen]       = useState(false);
  const [complete, setComplete] = useState(false);
  const [bp, setBp] = useState(_BP_DEFAULT);
  const [pd, setPd] = useState(_PD_DEFAULT);
  const [topicInput, setTopicInput] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/account/me`);
        const d = res.data || {};
        setBp({ ..._BP_DEFAULT, ...(d.brand_profile || {}) });
        setPd({ ..._PD_DEFAULT, ...(d.plan_defaults || {}) });
        setComplete(!!d.has_brand_profile);
      } catch {
        /* noch kein Account/Profil — Defaults bleiben */
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const setField = (k, v) => setBp((p) => ({ ...p, [k]: v }));
  const addTopic = (raw) => {
    const v = (raw ?? topicInput).trim();
    if (v && !bp.topics.includes(v)) setBp((p) => ({ ...p, topics: [...p.topics, v] }));
    setTopicInput("");
  };
  const removeTopic = (t) => setBp((p) => ({ ...p, topics: p.topics.filter((x) => x !== t) }));
  const toggleArr = (setter, key, val) =>
    setter((p) => ({
      ...p,
      [key]: p[key].includes(val) ? p[key].filter((x) => x !== val) : [...p[key], val],
    }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...bp, goals: pd.goals, intensity: pd.intensity, platforms: pd.platforms };
      const res = await axios.patch(`${API}/account/brand-profile`, payload);
      setComplete(!!res.data?.has_brand_profile);
      toast.success("Markenprofil gespeichert — fließt direkt in deinen 1-Klick-Plan ein.");
      setOpen(false);
    } catch {
      toast.error("Konnte das Markenprofil nicht speichern. Bitte erneut versuchen.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white rounded-xl border border-stone-200 overflow-hidden">
      {/* Header */}
      <div className="p-5 flex items-start gap-3">
        <div className="w-9 h-9 bg-orange-100 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
          <Sparkles size={18} className="text-orange-500" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <p className="font-semibold text-stone-800">Markenprofil</p>
            {complete ? (
              <span className="inline-flex items-center gap-1 text-xs font-medium text-green-600">
                <CheckCircle size={12} /> Aktiv
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 text-xs font-medium text-amber-600">
                <AlertTriangle size={12} /> Unvollständig
              </span>
            )}
          </div>
          <p className="text-xs text-stone-500 mt-0.5">
            Unternehmen, Angebot, Zielgruppe, Themen &amp; Tonalität — fließt automatisch in deinen
            1-Klick-Monatsplan ein.
          </p>
        </div>
        <button
          onClick={() => setOpen((o) => !o)}
          disabled={loading}
          className="flex-shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm
                     font-medium text-fox-fire border border-orange-200 hover:bg-orange-50
                     transition-colors disabled:opacity-40"
        >
          {loading ? <Loader2 size={14} className="animate-spin" /> : open ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          {open ? "Schließen" : "Bearbeiten"}
        </button>
      </div>

      {/* Kompakte Zusammenfassung wenn zu */}
      {!open && !loading && (
        <div className="px-5 pb-5 -mt-1 flex flex-wrap gap-1.5">
          {bp.company_name && (
            <span className="text-xs text-stone-700 font-medium">{bp.company_name}</span>
          )}
          {bp.topics.slice(0, 6).map((t) => (
            <span key={t} className="px-2 py-0.5 bg-stone-100 rounded-full text-xs text-stone-600">
              {t}
            </span>
          ))}
          {!bp.company_name && !bp.topics.length && (
            <span className="text-xs text-stone-400">
              Noch nichts hinterlegt — „Bearbeiten" öffnen und ausfüllen.
            </span>
          )}
        </div>
      )}

      {/* Editor */}
      {open && (
        <div className="px-5 pb-5 space-y-5 border-t border-stone-100 pt-5">
          {/* Unternehmen + Website */}
          <div className="grid sm:grid-cols-2 gap-4">
            <label className="block">
              <span className="text-xs font-medium text-stone-500">Unternehmen / Marke</span>
              <input
                value={bp.company_name}
                onChange={(e) => setField("company_name", e.target.value)}
                placeholder="z. B. Praxis Sonnenschein"
                className="mt-1 w-full px-3 py-2 rounded-lg border border-stone-200 text-sm focus:outline-none focus:ring-2 focus:ring-orange-300"
              />
            </label>
            <label className="block">
              <span className="text-xs font-medium text-stone-500">Website (optional)</span>
              <input
                value={bp.website}
                onChange={(e) => setField("website", e.target.value)}
                placeholder="https://…"
                className="mt-1 w-full px-3 py-2 rounded-lg border border-stone-200 text-sm focus:outline-none focus:ring-2 focus:ring-orange-300"
              />
            </label>
          </div>

          {/* Angebot */}
          <label className="block">
            <span className="text-xs font-medium text-stone-500">Was bietest du an?</span>
            <textarea
              value={bp.offer}
              onChange={(e) => setField("offer", e.target.value)}
              rows={2}
              placeholder="Kurz beschrieben: Produkte, Leistungen, Schwerpunkte"
              className="mt-1 w-full px-3 py-2 rounded-lg border border-stone-200 text-sm focus:outline-none focus:ring-2 focus:ring-orange-300 resize-none"
            />
          </label>

          {/* Zielgruppe */}
          <label className="block">
            <span className="text-xs font-medium text-stone-500">Für wen? (Zielgruppe)</span>
            <input
              value={bp.audience}
              onChange={(e) => setField("audience", e.target.value)}
              placeholder="z. B. Familien in der Region, kleine Unternehmen…"
              className="mt-1 w-full px-3 py-2 rounded-lg border border-stone-200 text-sm focus:outline-none focus:ring-2 focus:ring-orange-300"
            />
          </label>

          {/* Themen */}
          <div>
            <span className="text-xs font-medium text-stone-500">Content-Themen</span>
            <div className="mt-1 flex flex-wrap gap-1.5 mb-2">
              {bp.topics.map((t) => (
                <span key={t} className="inline-flex items-center gap-1 px-2 py-1 bg-orange-50 border border-orange-200 rounded-full text-xs text-orange-700">
                  {t}
                  <button type="button" onClick={() => removeTopic(t)} className="hover:text-orange-900">
                    <XCircle size={12} />
                  </button>
                </span>
              ))}
            </div>
            <div className="flex gap-2 mb-2">
              <input
                value={topicInput}
                onChange={(e) => setTopicInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addTopic(); } }}
                placeholder="Eigenes Thema + Enter"
                className="flex-1 px-3 py-2 rounded-lg border border-stone-200 text-sm focus:outline-none focus:ring-2 focus:ring-orange-300"
              />
              <button type="button" onClick={() => addTopic()} className="px-3 py-2 rounded-lg border border-stone-200 text-sm text-stone-600 hover:border-orange-300">
                Hinzufügen
              </button>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {BP_TOPIC_SUGGESTIONS.filter((s) => !bp.topics.includes(s)).map((s) => (
                <Chip key={s} active={false} onClick={() => addTopic(s)}>+ {s}</Chip>
              ))}
            </div>
          </div>

          {/* Tonalität */}
          <div>
            <span className="text-xs font-medium text-stone-500">Tonalität</span>
            <div className="mt-1 flex flex-wrap gap-1.5">
              {BP_TONES.map((t) => (
                <Chip key={t.id} active={bp.tone === t.id} onClick={() => setField("tone", t.id)}>
                  {t.label}
                </Chip>
              ))}
            </div>
          </div>

          <div className="border-t border-stone-100 pt-4">
            <p className="text-xs font-semibold text-stone-500 uppercase tracking-wide mb-3">
              Plan-Voreinstellungen (für den 1-Klick-Plan)
            </p>

            {/* Ziele */}
            <div className="mb-4">
              <span className="text-xs font-medium text-stone-500">Ziele</span>
              <div className="mt-1 flex flex-wrap gap-1.5">
                {BP_GOALS.map((g) => (
                  <Chip key={g.id} active={pd.goals.includes(g.id)} onClick={() => toggleArr(setPd, "goals", g.id)}>
                    {g.label}
                  </Chip>
                ))}
              </div>
            </div>

            {/* Intensität */}
            <div className="mb-4">
              <span className="text-xs font-medium text-stone-500">Intensität</span>
              <div className="mt-1 flex flex-wrap gap-1.5">
                {BP_INTENSITIES.map((i) => (
                  <Chip key={i.id} active={pd.intensity === i.id} onClick={() => setPd((p) => ({ ...p, intensity: i.id }))}>
                    {i.label}
                  </Chip>
                ))}
              </div>
            </div>

            {/* Plattformen */}
            <div>
              <span className="text-xs font-medium text-stone-500">Kanäle</span>
              <div className="mt-1 flex flex-wrap gap-1.5">
                {BP_PLATFORMS.map((pl) => (
                  <Chip key={pl.id} active={pd.platforms.includes(pl.id)} onClick={() => toggleArr(setPd, "platforms", pl.id)}>
                    {pl.label}
                  </Chip>
                ))}
              </div>
            </div>
          </div>

          {/* Speichern */}
          <div className="flex items-center gap-3 pt-1">
            <button
              onClick={save}
              disabled={saving}
              className="inline-flex items-center gap-2 px-4 py-2 bg-fox-fire text-white rounded-lg text-sm font-medium hover:bg-orange-600 transition-colors disabled:opacity-40"
            >
              {saving ? <Loader2 size={15} className="animate-spin" /> : <CheckCircle size={15} />}
              {saving ? "Speichere…" : "Markenprofil speichern"}
            </button>
            <button
              onClick={() => setOpen(false)}
              disabled={saving}
              className="text-sm text-stone-500 hover:text-stone-700"
            >
              Abbrechen
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Settings Page ────────────────────────────────────────────────────────────

const SettingsPage = () => {
  const [health, setHealth] = useState(null);
  const [oauthStatus, setOauthStatus] = useState(null);
  const [platformFailures, setPlatformFailures] = useState({});
  const schedulerRef = useRef(null);

  // Eine einzige usePlanSelection-Instanz für die gesamte SettingsPage —
  // selectedPlanId wird als Prop an CurrentPlanSection und PlanComparisonSection
  // übergeben, damit Plan-Wechsel beide Sektionen synchron aktualisiert.
  const { selectedPlanId, selectPlan } = usePlanSelection();
  const hasExplicitPlan = selectedPlanId !== null;

  // Master-Status backend-gesteuert (gleiche Quelle wie Sidebar: /account/me → is_master).
  // FAIL-CLOSED wie in frontend/src/App.js: null = Antwort steht noch aus und zählt als
  // „kein Master". Vorher `useState(true)` + `is_master !== false` — dadurch blitzten
  // Betreiber-Sektionen (Test Mode, Visual-Debug, Auto-Scheduler) beim Kunden auf,
  // solange /account/me noch lief oder fehlschlug.
  // Admin-Sektionen (Test Mode) erscheinen nur, wenn der Account wirklich Master ist
  // UND die Master-Ansicht aktiv ist (View-Toggle, gleicher Key wie Sidebar).
  const [isMaster, setIsMaster] = useState(null);
  const isAdmin = isMaster === true && (localStorage.getItem('foerderfuchs_view') || 'admin') === 'admin';

  // OAuth-Callback-Params werden jetzt in /kanale (SocialConnectPage) verarbeitet.
  // Die alten ?linkedin=connected / ?tiktok=connected / ?facebook=connected Redirects
  // wurden durch einheitliche ?connected=<platform> und ?error=<platform>&message=<code>
  // Parameter ersetzt. Kein Handler hier nötig.

  // Stripe-Rückkehr (?checkout=success): plan_status=active setzt der Webhook ASYNCHRON —
  // beim Redirect kann er noch nicht durch sein. Daher /billing/plan kurz pollen und erst
  // bei purchased=true den Client-Tarif synchronisieren + Erfolg melden. Danach Param weg.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("checkout") !== "success") return;
    const sessionId = params.get("session_id");
    let cancelled = false;
    let confirmed = false;
    const cleanUrl = () => window.history.replaceState({}, "", window.location.pathname + "#plan-management");

    const succeed = (tier) => {
      confirmed = true;
      if (tier && tier !== "free") {
        try { localStorage.setItem("foerderfuchs_current_plan", tier); } catch (_) {}
      }
      toast.success("Zahlung erfolgreich — dein Paket ist aktiv. Alle Kanäle sind freigeschaltet.");
      cleanUrl();
    };

    const poll = async (attempt) => {
      if (cancelled) return;
      try {
        const res = await axios.get(`${API}/billing/plan`);
        if (res.data?.purchased === true) { succeed(res.data?.tier); return; }
      } catch (_) { /* transient — weiter pollen */ }
      if (attempt < 8) {
        setTimeout(() => poll(attempt + 1), 1500);
      } else if (!confirmed) {
        // Webhook nicht durch → serverseitiger Reconcile-Fallback direkt bei Stripe.
        if (sessionId) {
          try {
            const rec = await axios.post(`${API}/billing/reconcile?session_id=${encodeURIComponent(sessionId)}`);
            if (rec.data?.activated) { succeed(rec.data?.tier); return; }
          } catch (_) { /* ignorieren — ehrliche Fallback-Meldung unten */ }
        }
        toast.success("Danke für deinen Kauf! Dein Paket wird in Kürze aktiv — lade die Seite gleich neu.");
        cleanUrl();
      }
    };
    poll(0);
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const [healthRes, oauthRes, failRes] = await Promise.all([
          axios.get(`${API}/health`),
          // Einheitliche Status-API (social_connect) statt alter social-oauth/status
          axios.get(`${API}/social-connect/status`),
          axios.get(`${API}/auto-scheduler/platform-failures`),
        ]);
        setHealth(healthRes.data);
        // Daten in legacy-Format überführen das ConnectionStatusBlock erwartet
        const platforms = oauthRes.data?.platforms || {};
        const toConnected = (s) => ["ready_to_post", "connected"].includes(s);
        setOauthStatus({
          facebook: {
            connected: toConnected(platforms.facebook?.status),
            needs_refresh: ["reconnect_needed", "token_expired"].includes(platforms.facebook?.status),
            page_name: platforms.facebook?.page_name || platforms.facebook?.profile_name,
          },
          linkedin: {
            connected: toConnected(platforms.linkedin?.status),
            status: platforms.linkedin?.status,
            profile_name: platforms.linkedin?.profile_name,
          },
          tiktok: {
            connected: toConnected(platforms.tiktok?.status),
            status: platforms.tiktok?.status,
            profile_name: platforms.tiktok?.profile_name,
            not_configured: platforms.tiktok?.error_type === "not_configured",
          },
          google_business: {
            connected: toConnected(platforms.google_business?.status),
          },
        });
        setPlatformFailures(failRes.data?.platform_failures || {});
      } catch (err) {
        { /* Fetch fehlgeschlagen – kein kritischer Fehler */ }
      }
      // Master-Status separat (eigener try, damit ein Fehler hier die Status-Daten nicht kippt)
      try {
        const meRes = await axios.get(`${API}/account/me`);
        setIsMaster(meRes.data?.is_master === true);   // fail-closed
      } catch {
        setIsMaster(false);                            // fail-closed: kein Master
      }
    };
    fetchAll();
  }, []);

  // Verbindungsverwaltung erfolgt jetzt über /kanale (SocialConnectPage)

  const scrollToScheduler = () => {
    schedulerRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const gmbConnected = oauthStatus?.google_business?.connected;

  return (
    <div className="space-y-5 animate-fade-in">
      <div>
        <h1 className="font-heading text-3xl font-bold text-stone-900">Einstellungen</h1>
        <p className="text-stone-500 mt-1">System-Status, Verbindungen &amp; Auto-Scheduler</p>
      </div>

      {/* 1. Fehler-Warnbanner */}
      <FailureWarningBanner failures={platformFailures} onScrollToScheduler={scrollToScheduler} />

      {/* 2. Onboarding-Box */}
      <OnboardingBox />

      {/* 2·Marke. Markenprofil bearbeiten — speist den 1-Klick-Monatsplan.
          Anker-Ziel des Header-Loops „Markenprofil" (scroll-mt für fixen Header). */}
      <div id="markenprofil" className="scroll-mt-32">
        <BrandProfileEditor />
      </div>

      {/* 2a. Aktueller Plan & Trial-Status */}
      <CurrentPlanSection
        selectedPlanId={selectedPlanId}
        hasExplicitPlan={hasExplicitPlan}
      />

      {/* 2b. Plan wechseln / Kompakter Plan-Vergleich */}
      <PlanComparisonSection
        selectedPlanId={selectedPlanId}
        hasExplicitPlan={hasExplicitPlan}
        selectPlan={selectPlan}
      />

      {/* 2c. Website-Analyse */}
      <WebsiteInsightsPanel />

      {/* 3. Verbindungsstatus-Block */}
      <ConnectionStatusBlock
        oauthStatus={oauthStatus}
        gmbStatus={gmbConnected}
      />

      {/* 4. System-Status */}
      <div className="bg-white rounded-xl border border-stone-200 p-6">
        <h2 className="font-heading text-lg font-bold mb-4">System-Status</h2>
        {health ? (
          <div className="space-y-3">
            <StatusRow label="Claude AI (Datenbasis)" connected={health.claude_configured} />
            <StatusRow label="Groq AI (Content)" connected={health.groq_configured} />
            <StatusRow label="Longcat AI" connected={health.longcat_configured} />
          </div>
        ) : (
          <div className="flex items-center justify-center py-8">
            <RefreshCw className="animate-spin text-fox-fire" size={24} />
          </div>
        )}
      </div>

      {/* 5. Kanalverbindungen → /kanale */}
      <div className="bg-white rounded-xl border border-stone-200 p-6 flex items-center justify-between gap-4">
        <div className="flex items-start gap-4">
          <div className="w-10 h-10 bg-fox-fire rounded-xl flex items-center justify-center flex-shrink-0">
            <Link2 size={18} className="text-white" />
          </div>
          <div>
            <h2 className="font-semibold text-stone-800">Social Media Kanäle verbinden</h2>
            <p className="text-sm text-stone-500 mt-0.5">
              Facebook, Instagram, LinkedIn, TikTok und Google Business verwaltest du auf der Seite „Kanäle verbinden".
            </p>
          </div>
        </div>
        <a
          href="/kanale"
          className="flex-shrink-0 flex items-center gap-2 px-4 py-2 bg-fox-fire text-white rounded-xl text-sm font-medium hover:bg-orange-600 transition-colors"
        >
          <ArrowRight size={15} />
          Zu den Kanälen
        </a>
      </div>

      {/* 7. Auto-Scheduler — Betreiber-Werkzeug (installationsweiter Dienst).
             Für Kunden ausgeblendet: toggle/run-now sind serverseitig owner-only (403),
             und die Kunden-Automatik läuft ohnehin über Auto-Veröffentlichen. */}
      {isMaster === true && <AutoSchedulerSection schedulerRef={schedulerRef} />}

      {/* 8. Datenschutz & meine Daten (DSGVO: Export + Löschung) */}
      <ApiKeysSection />

      <PasswordSection />

      <PrivacyDataSection />

      {/* 9. Test Mode – nur Admin */}
      {isAdmin && <TestModeSection />}

      {/* 10. Visuelles Debug-Panel (intern) — nur Admin */}
      {isAdmin && <VisualDebugPanel />}
    </div>
  );
};

export default SettingsPage;
